/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/dynamicDestinationsApi.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    API service functions for Dynamic Destinations.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  DynamicDestination,
  ListDynamicDestinationsParams,
  ListDynamicDestinationsResponse,
  CreateDynamicDestinationPayload,
  UpdateDynamicDestinationPayload
} from '../types';

/**
 * Adapt API response to DynamicDestination
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function adaptDynamicDestination(payload: any): DynamicDestination {
  return {
    id: Number(payload.id),
    uuid: payload.uuid,
    domainId: payload.domainId ?? payload.domain_id ?? null,
    name: payload.name ?? '',
    sourceType: (payload.sourceType ?? payload.source_type ?? 'DB') as 'DB' | 'URL',
    sourceConfig: payload.sourceConfig ?? payload.source_config ?? '',
    queryTemplate: payload.queryTemplate ?? payload.query_template ?? null,
    matchField: payload.matchField ?? payload.match_field ?? '',
    defaultModule: payload.defaultModule ?? payload.default_module ?? null,
    defaultDestination: payload.defaultDestination ?? payload.default_destination ?? '',
    description: payload.description ?? null,
    active: Boolean(payload.active ?? payload.isActive ?? true),
    optionsCount: Number(
      payload.optionsCount ??
      payload.options_count ??
      (Array.isArray(payload.options) ? payload.options.length : 0),
    ),
    createdAt: payload.createdAt ?? payload.created_at ?? null,
    createdBy:
      payload.createdBy ?? payload.created_by ?? null
        ? Number(payload.createdBy ?? payload.created_by)
        : null,
    updatedAt: payload.updatedAt ?? payload.updated_at ?? null,
    updatedBy:
      payload.updatedBy ?? payload.updated_by ?? null
        ? Number(payload.updatedBy ?? payload.updated_by)
        : null,
    options: payload.options,
  };
}

/**
 * Fetch Dynamic Destinations with pagination and filters
 */
export async function fetchDynamicDestinations(
  params: ListDynamicDestinationsParams
): Promise<ListDynamicDestinationsResponse> {
  const queryParams = new URLSearchParams({
    page: (params.page ?? 1).toString(),
    limit: (params.limit ?? 25).toString(),
    sortBy: params.sortBy ?? 'name',
    sortOrder: params.sortOrder ?? 'asc',
    ...(params.search ? { search: params.search } : {}),
    ...(params.domainId != null ? { domainId: params.domainId.toString() } : {}),
  });

  const response = await fetch(
    `${buildApiUrl('/api/telephony/dynamic-destinations')}?${queryParams.toString()}`,
    { headers: getApiHeaders() },
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to fetch dynamic destinations');
  }

  return {
    success: result.success,
    data: Array.isArray(result.data)
      ? result.data.map(adaptDynamicDestination)
      : [],
    pagination:
      result.pagination ??
      ({
        page: params.page,
        limit: params.limit,
        total: 0,
        totalPages: 0,
      } as const),
  };
}

/**
 * Fetch single Dynamic Destination by ID
 */
export async function fetchDynamicDestinationById(id: string): Promise<DynamicDestination> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/dynamic-destinations/${id}`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const result = await response.json();
  if (!result?.success || !result?.data) {
    throw new Error(result?.message || 'Failed to load dynamic destination');
  }

  return adaptDynamicDestination(result.data);
}

/**
 * Create new Dynamic Destination
 */
export async function createDynamicDestination(
  payload: CreateDynamicDestinationPayload
): Promise<{ success: boolean; data: DynamicDestination; message?: string }> {
  const response = await fetch(
    buildApiUrl('/api/telephony/dynamic-destinations'),
    {
      method: 'POST',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  const result = await response.json();
  if (!response.ok || !result?.success) {
    throw new Error(result?.message || 'Failed to save dynamic destination');
  }

  return {
    success: result.success,
    data: adaptDynamicDestination(result.data),
    message: result.message
  };
}

/**
 * Update Dynamic Destination
 */
export async function updateDynamicDestination(
  id: string,
  payload: UpdateDynamicDestinationPayload
): Promise<{ success: boolean; data: DynamicDestination; message?: string }> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/dynamic-destinations/${id}`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  const result = await response.json();
  if (!response.ok || !result?.success) {
    throw new Error(result?.message || 'Failed to save dynamic destination');
  }

  return {
    success: result.success,
    data: adaptDynamicDestination(result.data),
    message: result.message
  };
}

/**
 * Delete Dynamic Destination
 */
export async function deleteDynamicDestination(id: string): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/dynamic-destinations/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    let message = 'Failed to delete dynamic destination';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }
}

